import type { ListPillItem, PillItem } from "@search/api/types";
import { BoardBasisFilter } from "@search/store/filters/BoardBasisFilter";
import { BookOnlineFilter } from "@search/store/filters/BookOnlineFilter";
import { DateFilter } from "@search/store/filters/DateFilter";
import { EurotunnelFilter } from "@search/store/filters/EurotunnelFilter";
import { ExtraYardsFilter } from "@search/store/filters/ExtraYardsFilter";
import { FlightsFilter } from "@search/store/filters/FlightsFilter";
import { FourBallFilter } from "@search/store/filters/FourBallFilter";
import { GroupSizeFilter } from "@search/store/filters/GroupSizeFilter";
import { LocationFilter } from "@search/store/filters/LocationFilter";
import { NightFilter } from "@search/store/filters/NightFilter";
import { OnlineExclusiveFilter } from "@search/store/filters/OnlineExclusiveFilter";
import { PageFilter } from "@search/store/filters/PageFilter";
import { PriceFilter } from "@search/store/filters/PriceFilter";
import { RegionFilter } from "@search/store/filters/RegionFilter";
import { ReviewsFilter } from "@search/store/filters/ReviewsFilter";
import { RoundFilter } from "@search/store/filters/RoundFilter";
import { SortFilter } from "@search/store/filters/SortFilter";
import { TopPicksFilter } from "@search/store/filters/TopPicksFilter";
import { ToursFilter } from "@search/store/filters/ToursFilter";
import type { Filters, URLParams } from "@search/store/types";

class FilterSet {
  filters: Filters;

  boardBasisFilter: BoardBasisFilter;
  bookOnlineFilter: BookOnlineFilter;
  dateFilter: DateFilter;
  eurotunnelFilter: EurotunnelFilter;
  extraYardsFilter: ExtraYardsFilter;
  flightsFilter: FlightsFilter;
  fourBallFilter: FourBallFilter;
  groupSizeFilter: GroupSizeFilter;
  locationFilter: LocationFilter;
  nightFilter: NightFilter;
  onlineExclusiveFilter: OnlineExclusiveFilter;
  pageFilter: PageFilter;
  priceFilter: PriceFilter;
  regionFilter: RegionFilter;
  reviewsFilter: ReviewsFilter;
  roundFilter: RoundFilter;
  sortFilter: SortFilter;
  topPicksFilter: TopPicksFilter;
  toursFilter: ToursFilter;

  constructor(filters: Filters) {
    this.filters = filters;

    this.boardBasisFilter = new BoardBasisFilter(filters.boardBasis);
    this.bookOnlineFilter = new BookOnlineFilter(filters.bookOnline);
    this.dateFilter = new DateFilter(filters.date);
    this.eurotunnelFilter = new EurotunnelFilter(filters.eurotunnel);
    this.extraYardsFilter = new ExtraYardsFilter(filters.extraYards);
    this.flightsFilter = new FlightsFilter(filters.flights);
    this.fourBallFilter = new FourBallFilter(filters.fourBall);
    this.groupSizeFilter = new GroupSizeFilter(filters.groupSize);
    this.locationFilter = new LocationFilter(filters.location);
    this.nightFilter = new NightFilter(filters.nights);
    this.onlineExclusiveFilter = new OnlineExclusiveFilter(
      filters.onlineExclusive,
    );
    this.pageFilter = new PageFilter(filters.page);
    this.priceFilter = new PriceFilter(filters.price);
    this.regionFilter = new RegionFilter(filters.regionIds);
    this.reviewsFilter = new ReviewsFilter(filters.reviews);
    this.roundFilter = new RoundFilter(filters.rounds);
    this.topPicksFilter = new TopPicksFilter(filters.topPicks);
    this.toursFilter = new ToursFilter(filters.tours);

    this.sortFilter = new SortFilter(filters.sort, this.locationFilter.isSet());
  }

  get pillFilters() {
    return [
      this.regionFilter,
      this.locationFilter,
      this.priceFilter,

      this.nightFilter,
      this.roundFilter,
      this.groupSizeFilter,

      this.dateFilter,
      this.boardBasisFilter,
      this.reviewsFilter,

      this.bookOnlineFilter,
      this.eurotunnelFilter,
      this.flightsFilter,
      this.onlineExclusiveFilter,
      this.topPicksFilter,
      this.extraYardsFilter,
      this.fourBallFilter,
      this.toursFilter,
    ];
  }

  apply(fn: (filterSet: FilterSet) => void) {
    const newFilters = new FilterSet(this.filters);

    newFilters.pageFilter.unset();
    fn(newFilters);

    return newFilters.buildURLParams();
  }

  buildURLParams() {
    const urlParams: Partial<URLParams> = {
      ...this.regionFilter.toURL(),
      ...this.locationFilter.toURL(),
      ...this.priceFilter.toURL(),
      ...this.nightFilter.toURL(),
      ...this.roundFilter.toURL(),
      ...this.groupSizeFilter.toURL(),
      ...this.dateFilter.toURL(),
      ...this.boardBasisFilter.toURL(),
      ...this.reviewsFilter.toURL(),
      ...this.bookOnlineFilter.toURL(),
      ...this.onlineExclusiveFilter.toURL(),
      ...this.topPicksFilter.toURL(),
      ...this.extraYardsFilter.toURL(),
      ...this.fourBallFilter.toURL(),
      ...this.eurotunnelFilter.toURL(),
      ...this.flightsFilter.toURL(),
      ...this.toursFilter.toURL(),

      ...this.sortFilter.toURL(),
      ...this.pageFilter.toURL(),
    };

    if (this.filters.days) {
      urlParams.days = this.filters.days.toString();
    }

    const queryString = new URLSearchParams(urlParams).toString();

    return `?${queryString}`;
  }

  clearAll() {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.boardBasisFilter.unset());
      newFilters.update(newFilters.bookOnlineFilter.unset());
      newFilters.update(newFilters.dateFilter.unset());
      newFilters.update(newFilters.eurotunnelFilter.unset());
      newFilters.update(newFilters.extraYardsFilter.unset());
      newFilters.update(newFilters.flightsFilter.unset());
      newFilters.update(newFilters.fourBallFilter.unset());
      newFilters.update(newFilters.locationFilter.unset());
      newFilters.update(newFilters.nightFilter.unset());
      newFilters.update(newFilters.onlineExclusiveFilter.unset());
      newFilters.update(newFilters.priceFilter.unset());
      newFilters.update(newFilters.regionFilter.unset());
      newFilters.update(newFilters.reviewsFilter.unset());
      newFilters.update(newFilters.roundFilter.unset());
      newFilters.update(newFilters.groupSizeFilter.unset());
      newFilters.update(newFilters.topPicksFilter.unset());
      newFilters.update(newFilters.toursFilter.unset());

      newFilters.update(newFilters.sortFilter.unset());
      newFilters.update(newFilters.pageFilter.unset());
    });
  }

  update(newFilters: Partial<Filters>) {
    return {
      ...this.filters,
      ...newFilters,
    };
  }

  applyBoardBasis(boardBasis: string, add: boolean) {
    return this.apply((newFilters: FilterSet) => {
      const newBoardBasisFilter = add
        ? newFilters.boardBasisFilter.add(boardBasis)
        : newFilters.boardBasisFilter.remove(boardBasis);

      newFilters.update(newBoardBasisFilter);
    });
  }

  clearBoardBasis() {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.boardBasisFilter.unset());
    });
  }

  includesBoardBasis(boardBasis: string) {
    return this.boardBasisFilter.includes(boardBasis);
  }

  applyBookOnline(bookOnline: boolean) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.bookOnlineFilter.set(bookOnline));
    });
  }

  getBookOnline() {
    return this.bookOnlineFilter.value;
  }

  applyEurotunnel(eurotunnel: boolean) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.eurotunnelFilter.set(eurotunnel));
    });
  }

  getEurotunnel() {
    return this.eurotunnelFilter.value;
  }

  applyExtraYards(extraYards: boolean) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.extraYardsFilter.set(extraYards));
    });
  }

  getExtraYards() {
    return this.extraYardsFilter.value;
  }

  applyFourBall(fourBall: boolean) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.fourBallFilter.set(fourBall));
    });
  }

  getFourBall() {
    return this.fourBallFilter.value;
  }

  applyFlights(flights: boolean) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.flightsFilter.set(flights));
    });
  }

  getFlights() {
    return this.flightsFilter.value;
  }

  applyOnlineExclusive(onlineExclusive: boolean) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.onlineExclusiveFilter.set(onlineExclusive));
    });
  }

  getOnlineExclusive() {
    return this.onlineExclusiveFilter.value;
  }

  applyTopPicks(topPicks: boolean) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.topPicksFilter.set(topPicks));
    });
  }

  getTopPicks() {
    return this.topPicksFilter.value;
  }

  applyTours(tours: boolean) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.toursFilter.set(tours));
    });
  }

  getTours() {
    return this.toursFilter.value;
  }

  applyDate(date: Date) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.dateFilter.set(date));
    });
  }

  clearDate() {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.dateFilter.unset());
    });
  }

  getDate() {
    return this.dateFilter.value;
  }

  getDateParam() {
    return this.dateFilter.toParam();
  }

  applyNights(nights: number) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.nightFilter.set(nights));
    });
  }

  clearNights() {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.nightFilter.unset());
    });
  }

  getNights() {
    return this.nightFilter.value;
  }

  clearNightsAndRounds() {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.nightFilter.unset());
      newFilters.update(newFilters.roundFilter.unset());
      newFilters.update(newFilters.groupSizeFilter.unset());
    });
  }

  applyPage(page: number) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.pageFilter.set(page));
    });
  }

  getPills() {
    return this.pillFilters.flatMap((filter) => filter.toPills()) as Array<
      ListPillItem | PillItem
    >;
  }

  removePill(name: string) {
    return this.apply((newFilters: FilterSet) => {
      const filter = newFilters.pillFilters.find(
        (filter) => filter.name === name,
      );
      filter && newFilters.update(filter.unset());
    });
  }

  applyPrice(price: Array<number>) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.priceFilter.set(price));
    });
  }

  clearPrice() {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.priceFilter.unset());
    });
  }

  getPrice() {
    return this.priceFilter.value;
  }

  applyRegion(id: number) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update({
        ...newFilters.regionFilter.set([id]),
        ...newFilters.locationFilter.unset(),
      });
    });
  }

  clearRegion() {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.regionFilter.unset());
    });
  }

  applyDistance(distance: number) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update({
        ...newFilters.regionFilter.unset(),
        ...newFilters.locationFilter.setDistance(distance),
        ...newFilters.sortFilter.setLocation(true),
      });
    });
  }

  applyLocation(slug: string) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update({
        ...newFilters.regionFilter.unset(),
        ...newFilters.locationFilter.setName(slug),
        ...newFilters.sortFilter.setLocation(true),
      });
    });
  }

  clearLocation() {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update({
        ...newFilters.locationFilter.unset(),
        ...newFilters.sortFilter.setLocation(false),
      });
    });
  }

  applyReviews(reviews: boolean) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.reviewsFilter.set(reviews));
    });
  }

  getReviews() {
    return this.reviewsFilter.value;
  }

  setReviews(reviews: boolean) {
    return this.update(this.reviewsFilter.set(reviews));
  }

  applyRounds(rounds: number) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.roundFilter.set(rounds));
    });
  }

  clearRounds() {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.roundFilter.unset());
    });
  }

  getRounds() {
    return this.roundFilter.value;
  }

  applyGroupSize(groupSize: number) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.groupSizeFilter.set(groupSize));
    });
  }

  clearGroupSize() {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.groupSizeFilter.unset());
    });
  }

  getGroupSize() {
    return this.groupSizeFilter.value;
  }

  applySort(value: string) {
    return this.apply((newFilters: FilterSet) => {
      newFilters.update(newFilters.sortFilter.set(value));
    });
  }

  getSort() {
    return this.sortFilter.value;
  }
}

export default FilterSet;
export { FilterSet };
